import { yupResolver } from "@hookform/resolvers/yup";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useContext, useEffect, useMemo, useCallback } from "react";
import { Col, Form, InputGroup, Row, Table } from "react-bootstrap";
import {
  Control,
  FieldError,
  FieldErrorsImpl,
  FormProvider,
  Merge,
  useFieldArray,
  UseFieldArrayRemove,
  useForm,
  UseFormGetValues,
  UseFormRegister,
} from "react-hook-form";
import { useParams } from "react-router-dom";
import * as yup from "yup";
import type { AccountsController } from "../../../../api/src/accounts/accounts.controller";
import type { EditableAccountTrading } from "../../../../api/src/accounts/accounts.service";
import type { SecurityRestriction } from "../../../../api/src/models/models.service";
import type { SerializedObject, UnpackResponse } from "../../../../api/src/lib";
import type { FirmsController } from "../../../../api/src/firms/firms.controller";
import Loading from "../../Loading";
import { NotificationContext } from "../../Notifications";
import {
  modelContainsCashSplit,
  useQueryAccountCashSetting,
} from "../../cash/lib";
import FirmDefaultMtaxRates from "../../components/FirmDefaultMtaxRates";
import FormError from "../../components/FormError";
import FormFieldError from "../../components/FormFieldError";
import LabelValuePair from "../../components/LabelValuePair";
import ModelLink from "../../components/ModelLink";
import SecurityLink from "../../components/SecurityLink";
import SubmitButton from "../../components/SubmitButton";
import {
  useAuthenticatedMutationAsync,
  useAuthenticatedFetch,
} from "../../lib/api";
import { InlineError, displayAccountName } from "../../lib/display";
import {
  getSchemaFieldLabel,
  idOptionalSelectorSchema,
  securityRestrictionSchema,
} from "../../lib/forms";
import { formatCurrencyComponent, formatPercent } from "../../lib/numbers";
import { useQueryModel, useQueryModels } from "../../models/lib";
import { useQueryHousehold } from "../household/lib";
import AccountAssetAllocationChart from "./AccountAssetAllocationChart";
import { AccountContext } from "./AccountInfo";

// The api sends these fields as explicit null rather than omitting them
// (APP-1535). yup 1.x refuses a null against a bare schema, so the resolver
// would reject every household or account that has one and handleSubmit
// would never fire. An empty control still means "none"; it now reads as
// null instead of an absent key, and the route writes the same NULL.
const nullableIdSchema = yup
  .number()
  .transform((value, originalValue) =>
    typeof originalValue === "undefined" ||
    originalValue === null ||
    originalValue === ""
      ? null
      : value,
  )
  .defined()
  .nullable()
  .default(null);

const nullableLotSelectionSchema = yup
  .string()
  .transform((value, originalValue) =>
    typeof originalValue === "undefined" ||
    originalValue === null ||
    originalValue === ""
      ? null
      : value,
  )
  .defined()
  .nullable()
  .default(null);

export const schema: yup.ObjectSchema<EditableAccountTrading> = yup.object({
  assignedModelId: nullableIdSchema.label("Assigned Model"),
  capitalGainsBudgetST: nullableIdSchema
    .min(0)
    .label("ST Capital Gains Budget"),
  capitalGainsBudgetLT: nullableIdSchema
    .min(0)
    .label("LT Capital Gains Budget"),
  lotSelectionMethod: nullableLotSelectionSchema.label("Lot Selection Method"),
  stGainTaxRate: nullableIdSchema.min(0).max(100).label("ST Cap Gains Rate"),
  ltGainTaxRate: nullableIdSchema.min(0).max(100).label("LT Cap Gains Rate"),
  taxBudget: nullableIdSchema.min(0).label("Tax Budget"),
  securityRestrictions: securityRestrictionSchema.required(),
  // holdings: yup
  //   .array()
  //   .required()
  //   .of(
  //     yup.object({
  //       restrictSecurity: yup.bool().required(),
  //       symbol: yup.string().required(),
  //       treatment: securityRestrictionTreatmentSchema,
  //     }),
  //   ),
});

const AccountModelTrading = () => {
  const { account } = useContext(AccountContext);
  const { accountId } = useParams();

  // Fetch household data
  const { data: household } = useQueryHousehold(account?.householdId ?? 0, {
    enabled: account?.householdId != null,
  });

  // Fetch firm data
  const { data: dataFirm } =
    useAuthenticatedFetch<
      SerializedObject<UnpackResponse<FirmsController["getCurrentFirm"]>>
    >("/firm/current");
  const firm = useMemo(
    () => (typeof dataFirm?.data === "undefined" ? undefined : dataFirm.data),
    [dataFirm?.data],
  );
  const form = useForm<EditableAccountTrading>({
    mode: "onBlur",
    resolver: yupResolver(schema),
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
    control,
    getValues,
    watch,
  } = form;

  useEffect(() => {
    if (typeof account !== "undefined") {
      reset({
        assignedModelId: account.assignedModelId,
        capitalGainsBudgetST: account.capitalGainsBudgetST,
        capitalGainsBudgetLT: account.capitalGainsBudgetLT,
        lotSelectionMethod: account.lotSelectionMethod ?? "",
        stGainTaxRate: account.stGainTaxRate,
        ltGainTaxRate: account.ltGainTaxRate,
        taxBudget: account.taxBudget,
        securityRestrictions: account.securityRestrictions,
        // holdings: holdings.map((holding) => {
        //   const symbol = holding.security?.identifier ?? "";
        //   const securityRestriction = account?.securityRestrictions.find(
        //     (restriction) => restriction.symbol === symbol,
        //   );
        //   const restrictSecurity = typeof securityRestriction !== "undefined";

        //   return {
        //     ...holding,
        //     symbol,
        //     restrictSecurity,
        //     treatment: securityRestriction?.treatment ?? "sell_no_tax",
        //   };
        // }),
      });
    }
  }, [account, reset]);

  const {
    fields: securityRestrictionFields,
    append: appendSecurityRestriction,
    remove: removeSecurityRestriction,
    replace: replaceSecurityRestriction,
  } = useFieldArray({
    name: "securityRestrictions",
    control,
    keyName: "_field_id",
  });

  const {
    isPending: isPendingModels,
    isError: isErrorModels,
    data: dataModels,
  } = useQueryModels();

  // R6 (doc-cash-split-01a05d5f47df200b): a model containing the cash split
  // cannot be assigned to an account configured with that cash split, and a
  // proposal that reaches the check fails. The refusal below is what keeps one
  // from being assigned here in the first place.
  //
  // The selected model is fetched one at a time through useQueryModel rather
  // than read out of useQueryModels above: getAllModels returns
  // primarySecurities: [] for every model by design
  // (api/src/models/models.service.ts), so a check built on dataModels would
  // silently never fire.
  //
  // The select hands back a string, and an unassigned model arrives as "" or
  // undefined; both coerce to something that is not a positive id, so nothing
  // is queried until a real model is chosen.
  const watchedAssignedModelId = Number(watch("assignedModelId"));
  const selectedModelId =
    watchedAssignedModelId > 0 ? watchedAssignedModelId : undefined;

  const { data: selectedModel } = useQueryModel(selectedModelId ?? -1, {
    enabled: typeof selectedModelId !== "undefined",
  });

  const { data: accountCashSetting } = useQueryAccountCashSetting(
    parseInt(accountId ?? ""),
    { enabled: typeof accountId !== "undefined" },
  );

  const cashSplitSymbol = accountCashSetting?.cashSplitSymbol;
  const cashSplitRefusal =
    typeof selectedModel === "undefined" ||
    !modelContainsCashSplit(selectedModel.primarySecurities, cashSplitSymbol)
      ? undefined
      : `Model ${selectedModel.name} contains ${cashSplitSymbol?.trim()}, this account's Cash Split. A model containing the cash split cannot be assigned to this account.`;

  const modelOptions = useMemo(
    () => [
      <option key={null} value="">
        None
      </option>,
      ...(dataModels ?? [])
        .sort((a, b) => (a.name < b.name ? -1 : a.name > b.name ? 1 : 0))
        .map((model) => (
          <option key={model.id} value={model.id}>
            {model.name}
          </option>
        )),
    ],
    [dataModels],
  );

  const updateAccount = useAuthenticatedMutationAsync<
    UnpackResponse<AccountsController["updateAccountTrading"]>
  >(
    // `PUT /accounts/:id/trading` is served at `/v1` only (APP-1653).
    `/v1/accounts/${accountId}/trading`,
    async (household: EditableAccountTrading) => ({
      method: "PUT",
      body: JSON.stringify(household),
    }),
  );

  const queryClient = useQueryClient();
  const notificationContext = useContext(NotificationContext);

  const onSubmit = useMutation({
    mutationFn: async (data: EditableAccountTrading) => {
      try {
        await updateAccount(data);
        queryClient.invalidateQueries({
          queryKey: [
            "accounts",
            parseInt(accountId ?? ""),
            { includeBalances: true },
          ],
        });
        notificationContext.pushNotification({
          id: `account-${accountId}`,
          header: "Account Updated",
          body: `${displayAccountName(
            account?.displayName,
            account?.displayNumber,
          )} updated`,
          variant: "success",
        });
      } catch (err) {
        console.error("Failed to save account", err);
        notificationContext.pushNotification({
          id: `account-${accountId}`,
          header: "Failed to Save Account",
          body: `${displayAccountName(
            account?.displayName,
            account?.displayNumber,
          )} was not saved`,
          variant: "danger",
        });
      }
    },
  });

  // Compute the effective lot selection method (for displaying the correct fields)
  const watchedLotSelectionMethod = watch("lotSelectionMethod");
  const effectiveLotSelectionMethod = useMemo(() => {
    if (watchedLotSelectionMethod) {
      return watchedLotSelectionMethod;
    }
    // Fall back through hierarchy: household -> firm -> HIFO
    return household?.lotSelectionMethod || firm?.lotSelectionMethod || "HIFO";
  }, [
    watchedLotSelectionMethod,
    household?.lotSelectionMethod,
    firm?.lotSelectionMethod,
  ]);

  const watchSecurityRestrictionsArray = watch("securityRestrictions", []);
  const controlledSecurityRestrictionFields = securityRestrictionFields.map(
    (field, index) => ({
      ...field,
      ...watchSecurityRestrictionsArray[index],
    }),
  );

  return (
    <FormProvider {...form}>
      <Form
        onSubmit={handleSubmit((data) =>
          // The refusal is rendered beside the selector for as long as the
          // collision holds, so a save that stops here has already said why.
          typeof cashSplitRefusal === "undefined"
            ? onSubmit.mutateAsync(data)
            : Promise.resolve(),
        )}
      >
        <Row>
          <Col xl={6} className="mb-3">
            <h3>Model Details</h3>
            <dl>
              <Row>
                <Col as="dt" lg={6}>
                  {getSchemaFieldLabel(schema.fields.assignedModelId)}
                </Col>
                <Col as="dd" lg={6}>
                  {isPendingModels ? (
                    <Loading />
                  ) : isErrorModels ? (
                    <InlineError>There was an error loading models</InlineError>
                  ) : (
                    <>
                      <ModelLink modelId={watch("assignedModelId")} />
                      <Form.Select
                        placeholder="Select assigned model"
                        {...register("assignedModelId")}
                      >
                        {modelOptions}
                      </Form.Select>
                    </>
                  )}
                  <FormFieldError field={errors.assignedModelId} />
                  {typeof cashSplitRefusal === "undefined" ? null : (
                    <FormError message={cashSplitRefusal} />
                  )}
                </Col>
              </Row>

              {!account?.isTaxable ? null : (
                <Row className="mb-3 mt-5">
                  <Col lg={6}>
                    <h5>Lot Selection Method</h5>
                    <Form.Select {...register("lotSelectionMethod")}>
                      <option value="">
                        Use firm default (
                        {firm?.lotSelectionMethod === "MTAX"
                          ? "MTAX"
                          : firm?.lotSelectionMethod === "HIFO"
                            ? "HIFO"
                            : "HIFO"}
                        )
                      </option>
                      <option value="HIFO">HIFO (Highest In, First Out)</option>
                      <option value="MTAX">MTAX (Minimum Tax)</option>
                    </Form.Select>
                    <FormFieldError field={errors.lotSelectionMethod} />
                  </Col>
                </Row>
              )}

              {account?.isTaxable && effectiveLotSelectionMethod === "HIFO" && (
                <Row className="mb-3">
                  <Col lg={4}>
                    <LabelValuePair
                      label="ST Capital Gains Budget"
                      value={
                        <InputGroup>
                          <InputGroup.Text>$</InputGroup.Text>
                          <Form.Control
                            type="number"
                            min={0}
                            step={0.01}
                            {...register("capitalGainsBudgetST")}
                          />
                        </InputGroup>
                      }
                      orientation="vertical"
                    />
                    <FormFieldError field={errors.capitalGainsBudgetST} />
                  </Col>
                  <Col lg={4}>
                    <LabelValuePair
                      label="LT Capital Gains Budget"
                      value={
                        <InputGroup>
                          <InputGroup.Text>$</InputGroup.Text>
                          <Form.Control
                            type="number"
                            min={0}
                            step={0.01}
                            {...register("capitalGainsBudgetLT")}
                          />
                        </InputGroup>
                      }
                      orientation="vertical"
                    />
                    <FormFieldError field={errors.capitalGainsBudgetLT} />
                  </Col>
                </Row>
              )}

              {account?.isTaxable &&
                effectiveLotSelectionMethod === "MTAX" &&
                !watchedLotSelectionMethod && (
                  <Row className="mb-3">
                    <FirmDefaultMtaxRates
                      variant="cols"
                      idPrefix="mtax-firm-defaults"
                      stGainTaxRate={firm?.stGainTaxRate ?? null}
                      ltGainTaxRate={firm?.ltGainTaxRate ?? null}
                      taxBudget={firm?.taxBudget ?? null}
                    />
                  </Row>
                )}

              {account?.isTaxable &&
                effectiveLotSelectionMethod === "MTAX" &&
                watchedLotSelectionMethod === "MTAX" && (
                  <Row className="mb-3">
                    <Col lg={4}>
                      <LabelValuePair
                        label={getSchemaFieldLabel(schema.fields.stGainTaxRate)}
                        value={
                          <InputGroup>
                            <Form.Control
                              type="number"
                              step={0.0001}
                              min={0}
                              max={100}
                              {...register("stGainTaxRate")}
                            />
                            <InputGroup.Text>%</InputGroup.Text>
                          </InputGroup>
                        }
                        orientation="vertical"
                      />
                      <FormFieldError field={errors.stGainTaxRate} />
                    </Col>
                    <Col lg={4}>
                      <LabelValuePair
                        label={getSchemaFieldLabel(schema.fields.ltGainTaxRate)}
                        value={
                          <InputGroup>
                            <Form.Control
                              type="number"
                              step={0.0001}
                              min={0}
                              max={100}
                              {...register("ltGainTaxRate")}
                            />
                            <InputGroup.Text>%</InputGroup.Text>
                          </InputGroup>
                        }
                        orientation="vertical"
                      />
                      <FormFieldError field={errors.ltGainTaxRate} />
                    </Col>
                    <Col lg={4}>
                      <LabelValuePair
                        label={getSchemaFieldLabel(schema.fields.taxBudget)}
                        value={
                          <InputGroup>
                            <InputGroup.Text>$</InputGroup.Text>
                            <Form.Control
                              type="number"
                              min={0}
                              step={0.01}
                              {...register("taxBudget")}
                            />
                          </InputGroup>
                        }
                        orientation="vertical"
                      />
                      <FormFieldError field={errors.taxBudget} />
                    </Col>
                  </Row>
                )}

              <Row className="mt-6">
                <Col as="dt" lg={6}>
                  Realized Gains YTD
                </Col>
                <Col as="dd">
                  {formatCurrencyComponent(
                    account?.performanceYTD?.realizedGainLoss ?? 0,
                  )}
                </Col>
              </Row>
              {/* <Row>
                <Col as="dt" lg={6}>
                  Last Rebalance Date
                </Col>
                <Col as="dd">
                  {typeof account?.lastRebalanceDate === "undefined" ||
                  account.lastRebalanceDate === null
                    ? naLabel
                    : account.lastRebalanceDate.toLocaleDateString()}
                </Col>
              </Row>
              <Row>
                <Col as="dt" lg={6}>
                  Pending Rebalance
                </Col>
                <Col as="dd">
                  {typeof account?.latestRebalanceDate === "undefined" ||
                  account.latestRebalanceDate === null ? (
                    naLabel
                  ) : (
                    <Link to={`/rebalances/${account.latestRebalanceId}`}>
                      {account.latestRebalanceDate.toLocaleDateString()}
                    </Link>
                  )}
                </Col>
              </Row> */}
            </dl>
          </Col>
          <Col>
            <h3>Allocation Details</h3>
            <AccountAssetAllocationChart />
          </Col>
        </Row>
        {account?.holdings && account.holdings.length > 0 && (
          <Row>
            <Col className="mb-3">
              <h3>Holdings Restrictions</h3>
              <SecurityRestrictionsHoldings
                fields={controlledSecurityRestrictionFields}
                replace={replaceSecurityRestriction}
                add={appendSecurityRestriction}
                remove={removeSecurityRestriction}
                register={register}
                control={control}
                getValues={getValues}
                errors={errors.securityRestrictions}
                holdings={account.holdings}
                account={account}
              />
            </Col>
          </Row>
        )}
        {/* <Row>
          <Col>
            <h3>Security Restrictions</h3>
            <SecurityRestrictions
              fields={controlledSecurityRestrictionFields}
              replace={replaceSecurityRestriction}
              add={appendSecurityRestriction}
              remove={removeSecurityRestriction}
              register={register}
              control={control}
              getValues={getValues}
              errors={errors.securityRestrictions}
            />
          </Col>
        </Row> */}
        <Row>
          <Col>
            <SubmitButton isSubmitting={isSubmitting} />
          </Col>
        </Row>
      </Form>
    </FormProvider>
  );
};

/**
 * A component for managing security restrictions from account holdings.
 * Allows users to select treatment (none, hold_only, sell_no_tax) for each holding.
 */
function SecurityRestrictionsHoldings<
  TForm extends { securityRestrictions: SecurityRestriction[] },
>({
  fields,
  replace,
  add,
  remove,
  register,
  control,
  getValues,
  errors,
  holdings,
  account,
}: {
  /** The collection of security restrictions */
  fields: (SecurityRestriction & { _field_id: string })[];
  /** The setter function for the fields prop */
  replace: (
    securityRestrictions: (SecurityRestriction & { _field_id: string })[],
  ) => void;
  add: (
    securityRestriction: SecurityRestriction & { _field_id: string },
  ) => void;
  remove: UseFieldArrayRemove;
  register: UseFormRegister<TForm>;
  control: Control<TForm>;
  getValues: UseFormGetValues<TForm>;
  errors?: Merge<
    FieldError,
    (Merge<FieldError, FieldErrorsImpl<SecurityRestriction>> | undefined)[]
  >;
  /** The holdings for the account */
  holdings: any[];
  /** The account data */
  account: any;
}) {
  const findRestrictionBySymbol = (symbol: string) => {
    return fields.find((restriction) => restriction.symbol === symbol);
  };

  useEffect(() => {
    if (account?.securityRestrictions) {
      replace(account.securityRestrictions);
    }
  }, [account.securityRestrictions]);

  // Simple function to update a restriction
  const updateRestriction = useCallback(
    (symbol: string, existingIndex: number, treatment: string) => {
      if (treatment === "none") {
        if (existingIndex >= 0) {
          remove(existingIndex);
        }
      } else {
        if (existingIndex >= 0) {
          const currentRestriction = fields[existingIndex];

          const updatedRestriction = {
            ...currentRestriction,
            treatment: treatment as "hold_only" | "sell_no_tax" | "exclude",
          };

          // Replace the restriction at the existing index
          const newRestrictions = [...fields];
          newRestrictions[existingIndex] = updatedRestriction;

          replace(newRestrictions);
        } else {
          // Add new restriction
          add({
            symbol,
            treatment: treatment as "hold_only" | "sell_no_tax" | "exclude",
            _field_id: "",
          });
        }
      }
    },
    [fields, remove, add, replace],
  );

  if (!holdings || holdings.length === 0) {
    return <div>No holdings to display</div>;
  }

  // Calculate total portfolio value for allocation percentages
  const totalValue = holdings
    .filter((holding) => holding.security?.securityType !== "Cash")
    .reduce((sum, holding) => sum + holding.value, 0);

  return (
    <Table className="form-table form-table-sm">
      <thead>
        <tr>
          <th>Security</th>
          <th>Allocation</th>
          <th>Value</th>
          <th>Unrealized Gain/Loss</th>
          <th>Treatment</th>
        </tr>
      </thead>
      <tbody>
        {holdings
          .filter((holding) => holding.security?.securityType !== "Cash")
          .map((holding, index) => {
            const symbol = holding.security?.identifier;

            if (!symbol) return null;

            const description = holding.security?.description;
            const allocationPercent =
              totalValue === 0 ? 0 : holding.value / totalValue;

            // Get current treatment from restrictionsMap or default to "none"
            const restriction = findRestrictionBySymbol(symbol);
            const treatment = restriction?.treatment || "none";

            return (
              <tr key={`holding-${index}`}>
                <td>
                  <SecurityLink symbol={symbol} description={description} />
                </td>
                <td>{formatPercent(allocationPercent, 2)}</td>
                <td>{formatCurrencyComponent(holding.value)}</td>
                <td>
                  <div className="d-flex flex-column align-items-end">
                    <div>
                      {formatCurrencyComponent(holding.unrealizedGainLoss)}
                    </div>
                    <div
                      className="small text-muted"
                      style={{ fontSize: "0.8rem", fontStyle: "italic" }}
                    >
                      {(() => {
                        const costBasis =
                          holding.value - holding.unrealizedGainLoss;
                        const percentage =
                          costBasis > 0
                            ? holding.unrealizedGainLoss / costBasis
                            : 0;
                        return formatPercent(percentage, 2);
                      })()}
                    </div>
                  </div>
                </td>
                <td>
                  <Form.Select
                    value={treatment}
                    onChange={(e) => {
                      const existingIndex = fields.findIndex(
                        (r) => r.symbol === symbol,
                      );
                      updateRestriction(symbol, existingIndex, e.target.value);
                    }}
                  >
                    <option value="none">None</option>
                    <option value="hold_only">Hold only</option>
                    <option value="sell_no_tax">
                      Sell if no tax consequences
                    </option>
                    <option value="exclude">Exclude</option>
                  </Form.Select>
                </td>
              </tr>
            );
          })}
      </tbody>
    </Table>
  );
}

export default AccountModelTrading;
